const { animate } = Motion;

const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Safer variants: object-of-arrays + Motion's transform shorthands
const VARIANTS = {
    'fade': () => ({ opacity: [0, 1] }),
    'fade-up': () => ({ opacity: [0, 1], y: [16, 0] }),
    'fade-down': () => ({ opacity: [0, 1], y: [-16, 0] }),
    'slide-left': () => ({ opacity: [0, 1], x: [24, 0] }),
    'slide-right': () => ({ opacity: [0, 1], x: [-24, 0] }),
    'zoom-in': () => ({ opacity: [0, 1], scale: [0.96, 1] })
};

const reveal = (el) => {
    if (prefersReduced) {
        el.style.opacity = 1;
        return;
    }

    const variant = el.dataset.anim || 'fade-up';
    const duration = parseFloat(el.dataset.duration || '0.7');
    const delay = parseFloat(el.dataset.delay || '0');
    const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
    const blur = el.dataset.blur === 'true'; // <-- add this in HTML to enable blur

    const baseKF = (VARIANTS[variant] || VARIANTS['fade-up'])();

    // Merge blur into motion if enabled
    const kf = blur
        ? { ...baseKF, filter: ['blur(6px)', 'blur(0px)'] }
        : baseKF;

    // Animate the container
    animate(el, kf, {
        duration,
        delay,
        easing,
        fill: 'both'
    });

    // Optional child stagger
    const stagger = parseFloat(el.dataset.stagger || '0');
    if (stagger > 0) {
        const kids = el.querySelectorAll('[data-anim-child]');
        kids.forEach((child, i) => {
            child.style.opacity = 0;
            animate(child, blur
                ? { ...baseKF, filter: ['blur(6px)', 'blur(0px)'] }
                : baseKF, {
                duration,
                delay: delay + i * stagger,
                easing,
                fill: 'both'
            });
        });
    }
};

const splitText = (el, { mode = 'chars', includeChildren = true } = {}) => {
    if (!el) return [];

    const originalNodes = Array.from(el.childNodes);
    const frag = document.createDocumentFragment();
    const pieces = [];

    // helper: create spans from a text string
    const pushTextPieces = (text) => {
      if (!text) return;
      if (mode === 'words') {
        text.split(/(\s+)/).forEach(tok => {
          if (/^\s+$/.test(tok)) {
            frag.appendChild(document.createTextNode(tok)); // preserve spacing
          } else {
            const s = document.createElement('span');
            s.textContent = tok;
            s.style.display = 'inline-block';
            s.style.opacity = 0;
            s.setAttribute('aria-hidden', 'true');
            frag.appendChild(s);
            pieces.push(s);
          }
        });
      } else {
        for (const ch of text) {
          if (ch === ' ') { frag.appendChild(document.createTextNode(' ')); continue; }
          const s = document.createElement('span');
          s.textContent = ch;
          s.style.display = 'inline-block';
          s.style.opacity = 0;
          s.setAttribute('aria-hidden', 'true');
          frag.appendChild(s);
          pieces.push(s);
        }
      }
    };

    // walk current child nodes
    originalNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        pushTextPieces(node.nodeValue);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // keep existing element
        if (includeChildren) {
          // animate the element itself as a piece (e.g., your orange <span>)
          node.style.opacity = 0;
          node.style.display = node.style.display || 'inline-block';
          pieces.push(node);
        }
        frag.appendChild(node); // append the original element unchanged
      }
    });

    // replace content with our fragment (which includes preserved elements)
    el.textContent = '';
    el.appendChild(frag);
    return pieces;
  }

const keyframesFor = (variant) => {
    switch (variant) {
        case 'down': return { opacity: [0, 1], y: [-12, 0] };
        case 'fade': return { opacity: [0, 1] };
        case 'up':
        default: return { opacity: [0, 1], y: [12, 0] };
    }
}

const revealSplit = (el) => {
    if (prefersReduced) {
        el.style.opacity = 1;
        return;
    }

    const mode = el.dataset.split || 'chars';
    const include  = (el.dataset.splitChildren || 'include') === 'include'; // 'include' | 'ignore'
    const variant = el.dataset.variant || 'up';
    const duration = parseFloat(el.dataset.duration || '0.8');
    const delay = parseFloat(el.dataset.delay || '0');
    const stagger = parseFloat(el.dataset.stagger || '0.03');
    const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';

    const parts = splitText(el, { mode, includeChildren: include });

    el.style.opacity = 1;                 // parent visible; children animate in
    el.style.willChange = 'opacity, transform';

    const kf = keyframesFor(variant);

    parts.forEach((span, i) => {
        animate(span, kf, {
            duration,
            delay: delay + i * stagger,
            easing,
            fill: 'both'
        });
    });
}


// Observe elements with data-anim
const els = document.querySelectorAll('[data-anim], [data-split]');
const io = new IntersectionObserver((entries, obs) => {
    entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        if (el.hasAttribute('data-split')) {
            revealSplit(el);
        } else {
            reveal(el);
        }
        obs.unobserve(el); // play once
    });
}, { rootMargin: '0px 0px -10% 0px', threshold: 0.2 });

els.forEach(el => {
    el.style.opacity = 0;                 // prevent flash
    el.style.willChange = 'opacity, transform';
    io.observe(el);
});
